import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Trash2,
  Mail,
  Clock,
  Crown,
  User
} from 'lucide-react';
import { useOrganization } from '@/hooks/useOrganization';
import { 
  getOrganizationInvitations, 
  revokeOrganizationInvitation 
} from '@/services/organizationService';
import { OrganizationInvitation } from '@/types/organization';
import { InviteMemberDialog } from './InviteMemberDialog';
import { toast } from '@/hooks/use-toast';

export function InvitationManager() {
  const [invitations, setInvitations] = useState<OrganizationInvitation[]>([]);
  const [loading, setLoading] = useState(false);
  const { currentOrganization, isAdmin } = useOrganization();

  const loadInvitations = async () => {
    if (!currentOrganization) return;
    
    try {
      setLoading(true);
      const organizationInvitations = await getOrganizationInvitations(currentOrganization.id);
      setInvitations(organizationInvitations);
    } catch (error) {
      console.error('Error loading invitations:', error);
      toast({
        title: "Error",
        description: "Failed to load invitations.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadInvitations();
  }, [currentOrganization]);

  const handleRevokeInvitation = async (invitation: OrganizationInvitation) => {
    if (!currentOrganization) return;
    
    try {
      await revokeOrganizationInvitation(currentOrganization.id, invitation.email);
      toast({
        title: "Invitation Revoked",
        description: "The invitation has been revoked.",
      });
      await loadInvitations(); // Refresh the list
    } catch (error) {
      console.error('Error revoking invitation:', error);
      toast({
        title: "Error",
        description: "Failed to revoke invitation.",
        variant: "destructive"
      });
    }
  };

  const handleInvitationCreated = () => {
    loadInvitations(); // Refresh the list when new invitation is created
  };

  if (!currentOrganization) {
    return null;
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5" />
              Pending Invitations ({invitations.length})
            </CardTitle>
            <CardDescription>
              Manage invitations to join your organization.
            </CardDescription>
          </div>
          {isAdmin && (
            <InviteMemberDialog onInvitationCreated={handleInvitationCreated} />
          )}
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="space-y-3">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="flex items-center gap-3 p-3 border rounded-lg">
                <Skeleton className="h-10 w-10 rounded-full" />
                <div className="flex-1">
                  <Skeleton className="h-4 w-48" />
                  <Skeleton className="h-3 w-32 mt-1" />
                </div>
                <Skeleton className="h-8 w-16" />
                <Skeleton className="h-8 w-8" />
              </div>
            ))}
          </div>
        ) : invitations.length > 0 ? (
          <div className="space-y-3">
            {invitations.map((invitation) => (
              <div key={invitation.id} className="flex items-center gap-3 p-3 border rounded-lg">
                <div className="flex-shrink-0">
                  <div className="h-10 w-10 rounded-full bg-muted flex items-center justify-center">
                    <Mail className="h-4 w-4 text-muted-foreground" />
                  </div>
                </div>
                
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="font-medium truncate">{invitation.email}</p>
                    <Badge variant={invitation.role === 'admin' ? 'default' : 'secondary'}>
                      {invitation.role === 'admin' ? (
                        <>
                          <Crown className="h-3 w-3 mr-1" />
                          Admin
                        </>
                      ) : (
                        <>
                          <User className="h-3 w-3 mr-1" />
                          Member
                        </>
                      )}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-1 text-sm text-muted-foreground">
                    <Clock className="h-3 w-3" />
                    <span>
                      Invited {invitation.createdAt.toDate?.()?.toLocaleDateString() || 'recently'}
                    </span>
                  </div>
                </div>
                
                {isAdmin && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleRevokeInvitation(invitation)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="rounded-lg border border-dashed p-6 text-center text-muted-foreground">
            <Mail className="h-8 w-8 mx-auto mb-3 opacity-50" />
            <p className="font-medium text-foreground">No pending invitations</p>
            {isAdmin && (
              <p className="text-sm mt-1 text-muted-foreground">
                Invite teammates when you're ready to expand access.
              </p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
